import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  DASHBOARD_DEFAULT_LAYOUT,
  DASHBOARD_WIDGET_TITLES,
  DashboardLayoutModel,
  DashboardLayoutWidget,
  DashboardLegacyRow
} from '../utils/dashboard.models';

@Injectable()
export class DashboardLayoutService {
  private readonly defaultTitleTemplateUrl = '/app-ds3/dashboard/angular-dashboard-framework-0.12.0/templates/dashboard-title.html';

  constructor(private readonly http: HttpClient) { }

  getLayout(): Observable<DashboardLayoutModel> {
    return this.http.get<any>('/api/userDashboardModel').pipe(
      map(payload => this.mapFromLegacy(payload)),
      catchError(() => of(DASHBOARD_DEFAULT_LAYOUT))
    );
  }

  saveLayout(layout: DashboardLayoutModel): Observable<void> {
    const rows = Array.isArray(layout.rows) && layout.rows.length
      ? this.normalizeRowsForSave(layout.rows)
      : this.buildRowsFromSelectedWidgets(layout.selectedWidgets);

    const modelToSave = {
      title: ' ',
      structure: '5-7',
      titleTemplateUrl: this.defaultTitleTemplateUrl,
      selectedWidgets: layout.selectedWidgets.map(widget => ({
        id: widget.id,
        name: DASHBOARD_WIDGET_TITLES[widget.id] || widget.id
      })),
      rows
    };

    return this.http.post<void>('/api/userDashboardModel', modelToSave);
  }

  private mapFromLegacy(payload: any): DashboardLayoutModel {
    const selectedWidgets = this.readSelectedWidgets(payload);

    if (!selectedWidgets.length) {
      return DASHBOARD_DEFAULT_LAYOUT;
    }

    return {
      selectedWidgets,
      rows: Array.isArray(payload?.rows) ? payload.rows : undefined
    };
  }

  private readSelectedWidgets(payload: any): DashboardLayoutWidget[] {
    const direct = (Array.isArray(payload?.selectedWidgets) ? payload.selectedWidgets : [])
      .map((widget: any) => ({ id: String(widget?.id || '').trim() }))
      .filter(widget => !!widget.id);

    if (direct.length > 0) {
      return direct;
    }

    const columns = Array.isArray(payload?.rows?.[0]?.columns)
      ? payload.rows[0].columns
      : [];

    if (!columns.length) {
      return [];
    }

    const widgetsByColumn: string[][] = columns.map((column: any) => {
      const widgets = Array.isArray(column?.widgets) ? column.widgets : [];
      return widgets
        .map((widget: any) => String(widget?.type || widget?.id || '').trim())
        .filter((id: string) => !!id);
    });

    const merged: string[] = [];
    const maxLength = Math.max(...widgetsByColumn.map(column => column.length), 0);

    for (let index = 0; index < maxLength; index++) {
      widgetsByColumn.forEach(column => {
        const id = column[index];
        if (id && !merged.includes(id)) {
          merged.push(id);
        }
      });
    }

    return merged.map(id => ({ id }));
  }

  private buildRowsFromSelectedWidgets(selectedWidgets: DashboardLayoutWidget[]): DashboardLegacyRow[] {
    const left: DashboardLayoutWidget[] = [];
    const right: DashboardLayoutWidget[] = [];

    selectedWidgets.forEach((widget, index) => {
      if (index % 2 === 0) {
        left.push(widget);
      } else {
        right.push(widget);
      }
    });

    return [{
      columns: [
        {
          cid: 'col-0',
          styleClass: 'col-md-5',
          widgets: left.map((widget, index) => ({
            type: widget.id,
            title: DASHBOARD_WIDGET_TITLES[widget.id] || widget.id,
            wid: `wid-0-${index}-${Date.now()}`,
            config: {}
          }))
        },
        {
          cid: 'col-1',
          styleClass: 'col-md-7',
          widgets: right.map((widget, index) => ({
            type: widget.id,
            title: DASHBOARD_WIDGET_TITLES[widget.id] || widget.id,
            wid: `wid-1-${index}-${Date.now()}`,
            config: {}
          }))
        }
      ]
    }];
  }

  private normalizeRowsForSave(rows: DashboardLegacyRow[]): DashboardLegacyRow[] {
    const now = Date.now();

    return rows.map((row, rowIndex) => {
      const columns = Array.isArray(row?.columns) ? row.columns : [];

      return {
        columns: columns.map((column, columnIndex) => {
          const widgets = Array.isArray(column?.widgets) ? column.widgets : [];

          return {
            cid: String(column?.cid || `col-${rowIndex}-${columnIndex}`),
            styleClass: column?.styleClass || (columnIndex === 0 ? 'col-md-5' : 'col-md-7'),
            widgets: widgets.map((widget, widgetIndex) => {
              const type = String(widget?.type || '').trim();
              return {
                type,
                title: widget?.title || DASHBOARD_WIDGET_TITLES[type] || type,
                wid: String(widget?.wid || `wid-${rowIndex}-${columnIndex}-${widgetIndex}-${now}`),
                config: widget?.config || {}
              };
            }).filter(widget => !!widget.type)
          };
        })
      };
    });
  }
}
